import React from 'react';
import { Button, Radio, Switch } from 'antd';
import ChartingLayoutPicker from 'components/ChartingLayoutPicker';
import {
    CHARTING_LAYOUTS,
    CHARTING_SECTIONS,
    TChartingPreference,
    TChartingPreferencePatch,
    TChartingSection,
    isSameChartingPreference,
} from 'types/chartingPreference';
import s from './s.module.less';

export type TApplyScope = 'session' | 'future';

type Props = {
    // what the session shows now; every change previews on the page right away
    value: TChartingPreference;
    onChange: (value: TChartingPreference) => void;
    // the session as it was when the menu opened
    before: TChartingPreference;
    // saved charting preferences (Calendar settings)
    saved?: TChartingPreference;
    applying?: boolean;
    onCancel: () => void;
    onApply: (scope: TApplyScope, patch: TChartingPreferencePatch) => void;
};

const SECTION_CHANGE_LABEL: Record<TChartingSection, string> = {
    PRESCRIPTIONS: 'Prescriptions',
    PRIVATE_NOTES: 'Private notes',
    DIAGNOSIS: 'Diagnosis',
    BILLING: 'Billing codes and billing team notes',
};

const layoutLabel = (layout: string) => CHARTING_LAYOUTS.find((x) => x.value === layout)?.label || '';

// The defaults that differ from the session: what "This and future sessions" saves, and its summary lines
export const getFuturePatch = (value: TChartingPreference, saved?: TChartingPreference) => {
    const patch: TChartingPreferencePatch = {};
    const lines: string[] = [];
    if (!saved) return { patch, lines };
    if (value.layout !== saved.layout) {
        patch.layout = value.layout;
        lines.push(`Default layout: ${layoutLabel(value.layout)}`);
    }
    CHARTING_SECTIONS.forEach(({ value: section }) => {
        if (value.sections[section] !== saved.sections[section]) {
            patch.sections = { ...patch.sections, [section]: value.sections[section] };
            lines.push(`${SECTION_CHANGE_LABEL[section]}: ${value.sections[section] ? 'shown' : 'hidden'}`);
        }
    });
    return { patch, lines };
};

const LayoutMenu = ({ value, onChange, before, saved, applying, onCancel, onApply }: Props) => {
    // "This session only" each time the menu opens (the menu remounts on open)
    const [scope, setScope] = React.useState<TApplyScope>('session');
    const future = getFuturePatch(value, saved);
    const canApply = scope === 'future' ? future.lines.length > 0 : !isSameChartingPreference(value, before);

    return (
            <div className={s.menu} role="dialog" aria-label="Layout">
                <ChartingLayoutPicker
                    className={s.layouts}
                    value={value.layout}
                    onChange={(layout) => onChange({ ...value, layout })}
                />
                <div className={s.caption}>
                    <b>{layoutLabel(value.layout)}</b>
                    {saved && !isSameChartingPreference(value, saved) && (
                        <a onClick={() => onChange({ layout: saved.layout, sections: { ...saved.sections } })}>
                            Use my defaults
                        </a>
                    )}
                </div>
                <div className={s.sections}>
                    {CHARTING_SECTIONS.map(({ value: section, label }) => (
                        <div key={section} className={s.row}>
                            <span>{label}</span>
                            <Switch
                                checked={value.sections[section]}
                                aria-label={label}
                                onChange={(checked) =>
                                    onChange({ ...value, sections: { ...value.sections, [section]: checked } })
                                }
                            />
                        </div>
                    ))}
                </div>
                <div className={s.foot}>
                    <span className={s.footLabel}>Apply to</span>
                    <Radio.Group className={s.scopes} value={scope} onChange={(e) => setScope(e.target.value)}>
                        <Radio value="session">
                            This session only
                            <span className={s.scopeDesc}>Your defaults stay the same.</span>
                        </Radio>
                        <Radio value="future">
                            This and future sessions
                            <span className={s.scopeDesc}>Updates your defaults for all patients.</span>
                        </Radio>
                    </Radio.Group>
                    {scope === 'future' && future.lines.length > 0 && (
                        <div className={s.changes}>
                            Your defaults will change:
                            <ul>
                                {future.lines.map((line) => (
                                    <li key={line}>{line}</li>
                                ))}
                            </ul>
                        </div>
                    )}
                    {scope === 'future' && future.lines.length === 0 && (
                        <div className={s.changes}>This session already matches your defaults.</div>
                    )}
                    <div className={s.actions}>
                        <Button onClick={onCancel}>Cancel</Button>
                        <Button
                            type="primary"
                            disabled={!canApply}
                            loading={applying}
                            onClick={() => onApply(scope, future.patch)}
                        >
                            {scope === 'future' ? 'Apply & save defaults' : 'Apply'}
                        </Button>
                    </div>
                </div>
            </div>
    );
};

export default LayoutMenu;
